import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { motion } from "framer-motion";

const LoadingOverlay = ({ loading, progress }) => {
  if (!loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-white/90 flex flex-col items-center justify-center z-50"
    >
      <div className="w-96 flex flex-col items-center gap-8">
        <div className="relative h-32 w-32">
          <svg className="absolute inset-0" viewBox="0 0 100 100">
            <circle
              className="stroke-gray-200"
              strokeWidth="8"
              fill="transparent"
              r="44"
              cx="50"
              cy="50"
            />
            <circle
              className="transition-all duration-300"
              strokeWidth="8"
              fill="transparent"
              r="44"
              cx="50"
              cy="50"
              strokeDasharray="276.46"
              strokeDashoffset={276.46 * (1 - progress / 100)}
              strokeLinecap="round"
              transform="rotate(-90 50 50)"
              stroke="#FC8F41"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-semibold">{Math.round(progress)}%</span>
          </div>
        </div>
        <div className="space-y-4 w-full">
          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-orng rounded-full"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="text-center space-y-2">
            <p className="text-lg font-semibold text-gray-800">
              Generating Your Dashboard
            </p>
            <p className="text-sm text-gray-600">
              This may take up to a minute as we analyze your idea and search for insights.
            </p>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-1">
          {progress < 33 && <p className="text-sm text-gray-600">Analyzing your idea...</p>}
          {progress >= 33 && progress < 66 && <p className="text-sm text-gray-600">Fetching Data...</p>}
          {progress >= 66 && <p className="text-sm text-gray-600">Finalizing your dashboard...</p>}
        </motion.div>
      </div>
    </motion.div>
  );
};

const EnterIdea = () => {
  const navigate = useNavigate();
  const [idea, setIdea] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("Worldwide");
  const [customCountry, setCustomCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const base_url = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (loading) {
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 1;
        });
      }, 450);

      return () => clearInterval(progressInterval);
    } else {
      setProgress(0);
    }
  }, [loading]);

  const handleRegionClick = (region) => {
    setSelectedRegion(region);
    if (region !== "Other") setCustomCountry("");
  };

  const handleSubmit = async () => {
    if (!idea.trim()) {
      alert("Please enter your idea before proceeding");
      return;
    }

    const regionText =
      selectedRegion === "Worldwide"
        ? "This idea applies worldwide."
        : `Based in: ${selectedRegion === "Other" ? customCountry.trim() : selectedRegion}.`;

    const fullIdea = `${idea.trim()} ${regionText}`;

    setLoading(true);
    try {
      const response = await axios.post(`${base_url}/create-workspace`, {
        userId: "c05b18fc-453f-43f2-9c58-39cf5dc637fb",
        idea: fullIdea,
      });

      const workspaceId = response.data.workspaceId;
      Cookies.set("workspaceId", workspaceId, { expires: 7 });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate("/overview");
    } catch (error) {
      console.error("Error creating workspace:", error);
      alert("Error creating workspace. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center items-center min-h-screen relative">
      <LoadingOverlay loading={loading} progress={progress} />

      <div className="w-2/3 text-center">
        <h1 className="text-5xl md:text-6xl font-semibold uppercase leading-snug tracking-wide w-4/5 mx-auto">
          What do you want to work on?
        </h1>

        <div className="mt-6 w-2/3 mx-auto">
          <textarea
            className="w-full p-4 text-lg border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orng focus:outline-none"
            rows={3}
            placeholder="Describe your idea in detail..."
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            style={{ minHeight: "80px", maxHeight: "300px", overflowY: "auto" }}
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {[ "Worldwide", "United States", "India", "Europe", "Asia", "Other"].map((region) => (
            <button
              key={region}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                selectedRegion === region ? "bg-orng text-white" : "bg-gray-200 text-gray-800"
              }`}
              onClick={() => handleRegionClick(region)}
            >
              {region}
            </button>
          ))}
        </div>

        {selectedRegion === "Other" && (
          <div className="mt-4">
            <input
              type="text"
              className="border border-gray-300 p-2 rounded-lg w-64 text-center"
              placeholder="Enter country name"
              value={customCountry}
              onChange={(e) => setCustomCountry(e.target.value)}
            />
          </div>
        )}

        <div className="mt-8">
          <p className="text-gray-600 md:text-lg w-2/3 mx-auto">
            Provide a brief but detailed description of your idea. Focus on the problem
            you aim to solve, the specific market or audience you're targeting, and
            what makes your approach unique. The more descriptive you are, the better
            insights we can provide.
          </p>
        </div>

        <button
          className="mt-6 px-6 py-3 bg-orng text-white font-semibold rounded-lg hover:bg-orange-600"
          onClick={handleSubmit}
        >
          Submit Idea
        </button>
      </div>
    </div>
  );
};

export default EnterIdea;
